<template>
  <div class="semester-management">
    <el-card>
      <template #header>
        <div class="card-header">
          <span>学期列表</span>
          <el-button type="primary" @click="showCreateDialog">
            <el-icon><Plus /></el-icon>
            新建学期
          </el-button>
        </div>
      </template>

      <el-table :data="semesters" v-loading="loading" stripe>
        <el-table-column prop="name" label="学期名称" min-width="200" />
        <el-table-column prop="start_date" label="开始日期" width="120" />
        <el-table-column prop="end_date" label="结束日期" width="120" />
        <el-table-column label="状态" width="100">
          <template #default="{ row }">
            <el-tag :type="row.is_active ? 'success' : 'info'">
              {{ row.is_active ? '当前学期' : '未激活' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="260" fixed="right">
          <template #default="{ row }">
            <el-button text type="primary" @click="editSemester(row)">编辑</el-button>
            <el-button text type="success" v-if="!row.is_active" @click="setActive(row)">设为当前</el-button>
            <el-button text type="warning" @click="openTeacherConfig(row)">
              <el-icon><UserFilled /></el-icon> 班级教师
            </el-button>
            <el-popconfirm title="确定删除此学期？" @confirm="deleteSemester(row.id)">
              <template #reference>
                <el-button text type="danger">删除</el-button>
              </template>
            </el-popconfirm>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- Create/Edit Dialog -->
    <el-dialog v-model="dialogVisible" :title="isEdit ? '编辑学期' : '新建学期'" width="500px">
      <el-form :model="form" label-width="100px" :rules="rules" ref="formRef">
        <el-form-item label="学期名称" prop="name">
          <el-input v-model="form.name" placeholder="如：2025-2026学年第一学期" />
        </el-form-item>
        <el-form-item label="开始日期" prop="start_date">
          <el-date-picker v-model="form.start_date" type="date" value-format="YYYY-MM-DD"
            placeholder="选择开始日期" style="width: 100%" />
        </el-form-item>
        <el-form-item label="结束日期" prop="end_date">
          <el-date-picker v-model="form.end_date" type="date" value-format="YYYY-MM-DD"
            placeholder="选择结束日期" style="width: 100%" />
        </el-form-item>
        <el-form-item label="设为当前">
          <el-switch v-model="form.is_active" />
        </el-form-item>
        
        <el-form-item label="配置年级">
          <div style="width: 100%;">
            <!-- Preset Category Checkboxes -->
            <div style="margin-bottom: 8px;">
              <div style="font-weight: bold; font-size: 12px; color: #909399; margin-bottom: 4px;">学段快捷选择：</div>
              <div style="display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 8px;">
                <el-button size="small" type="primary" plain @click="togglePresetSection('kindergarten')" style="padding: 4px 8px; font-size: 11px;">幼儿园</el-button>
                <el-button size="small" type="primary" plain @click="togglePresetSection('primary')" style="padding: 4px 8px; font-size: 11px;">小学</el-button>
                <el-button size="small" type="primary" plain @click="togglePresetSection('junior')" style="padding: 4px 8px; font-size: 11px;">初中</el-button>
                <el-button size="small" type="primary" plain @click="togglePresetSection('senior')" style="padding: 4px 8px; font-size: 11px;">高中</el-button>
                <el-button size="small" type="primary" plain @click="togglePresetSection('university')" style="padding: 4px 8px; font-size: 11px;">大学</el-button>
              </div>
            </div>

            <!-- Grade Grid / Checkboxes -->
            <div style="border: 1px solid #dcdfe6; border-radius: 6px; padding: 12px; max-height: 200px; overflow-y: auto; background-color: #fafafa;">
              <el-checkbox-group v-model="selectedGrades">
                <!-- Kindergarten -->
                <div class="grade-group-row">
                  <div class="grade-group-title">🧸 幼儿园</div>
                  <div class="grade-checkbox-list">
                    <el-checkbox v-for="g in presetGrades.kindergarten" :key="g" :label="g" class="grade-checkbox" />
                  </div>
                </div>
                
                <!-- Primary -->
                <div class="grade-group-row">
                  <div class="grade-group-title">🎒 小学</div>
                  <div class="grade-checkbox-list">
                    <el-checkbox v-for="g in presetGrades.primary" :key="g" :label="g" class="grade-checkbox" />
                  </div>
                </div>
                
                <!-- Junior High -->
                <div class="grade-group-row">
                  <div class="grade-group-title">🏫 初中</div>
                  <div class="grade-checkbox-list">
                    <el-checkbox v-for="g in presetGrades.junior" :key="g" :label="g" class="grade-checkbox" />
                  </div>
                </div>
                
                <!-- High School -->
                <div class="grade-group-row">
                  <div class="grade-group-title">🎓 高中</div>
                  <div class="grade-checkbox-list">
                    <el-checkbox v-for="g in presetGrades.senior" :key="g" :label="g" class="grade-checkbox" />
                  </div>
                </div>

                <!-- University -->
                <div class="grade-group-row">
                  <div class="grade-group-title">🏛️ 大学</div>
                  <div class="grade-checkbox-list">
                    <el-checkbox v-for="g in presetGrades.university" :key="g" :label="g" class="grade-checkbox" />
                  </div>
                </div>

                <!-- Custom / Added Grades -->
                <div v-if="customAddedGrades.length > 0" class="grade-group-row">
                  <div class="grade-group-title custom">✨ 自定义年级</div>
                  <div class="grade-checkbox-list">
                    <el-checkbox v-for="g in customAddedGrades" :key="g" :label="g" class="grade-checkbox custom-checkbox">
                      <span class="custom-grade-label">
                        {{ g }}
                        <span class="remove-btn" @click.prevent.stop="removeCustomGrade(g)">×</span>
                      </span>
                    </el-checkbox>
                  </div>
                </div>
              </el-checkbox-group>
            </div>

            <!-- Custom Grade Creator -->
            <div style="display: flex; gap: 8px; margin-top: 8px; align-items: center;">
              <el-input v-model="newCustomGrade" size="small" placeholder="添加自定义年级" style="width: 160px;" @keyup.enter="addCustomGrade" />
              <el-button size="small" type="primary" @click="addCustomGrade">添加</el-button>
            </div>
          </div>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitForm" :loading="submitting">确定</el-button>
      </template>
    </el-dialog>

    <!-- 班级教师配置 Dialog -->
    <el-dialog v-model="teacherDialogVisible" title="班级教师配置" width="900px" top="5vh">
      <div class="teacher-config-header">
        <el-alert type="info" :closable="false" show-icon>
          <template #title>每学期配置一次，记录各班级各科目对应的任课教师。未填写的科目不显示教师名。</template>
        </el-alert>
        <el-form inline style="margin-top: 12px">
          <el-form-item label="年级">
            <el-select v-model="teacherGrade" style="width: 120px" @change="buildTeacherGrid">
              <el-option v-for="g in currentSemesterGrades" :key="g" :label="g" :value="g" />
            </el-select>
          </el-form-item>
          <el-form-item label="班数">
            <el-input-number v-model="classCount" :min="1" :max="20" @change="buildTeacherGrid" style="width: 110px" />
          </el-form-item>
        </el-form>
      </div>

      <!-- 教师配置表格 -->
      <div v-loading="teacherLoading" class="teacher-grid-wrap">
        <el-table :data="teacherGrid" border size="small"
          :header-cell-style="{ background: '#f0f4ff', fontWeight: '600', textAlign: 'center' }">
          <el-table-column prop="class_name" label="班级" width="70" fixed align="center">
            <template #default="{ row }">
              <strong>{{ row.class_name }}</strong>
            </template>
          </el-table-column>
          <el-table-column v-for="subj in configSubjects" :key="subj" :label="subj" min-width="100">
            <template #default="{ row }">
              <el-input v-model="row.teachers[subj]" placeholder="教师姓名" size="small" clearable />
            </template>
          </el-table-column>
        </el-table>
      </div>

      <template #footer>
        <el-button @click="teacherDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="saveTeacherConfig" :loading="teacherSaving">保存配置</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { Plus, UserFilled } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import { semesterApi, classTeacherApi } from '@/api'

// ============ 学期管理 ============
const semesters = ref([])
const loading = ref(false)
const dialogVisible = ref(false)
const isEdit = ref(false)
const submitting = ref(false)
const formRef = ref(null)
const editingId = ref(null)

const defaultAvailableGrades = '一年级,二年级,三年级,四年级,五年级,六年级,初一,初二,初三,高一,高二,高三'
const form = ref({ name: '', start_date: '', end_date: '', is_active: false, available_grades: defaultAvailableGrades })

// ============ 年级配置数据 ============
const presetGrades = {
  kindergarten: ['小班', '中班', '大班'],
  primary: ['一年级', '二年级', '三年级', '四年级', '五年级', '六年级'],
  junior: ['初一', '初二', '初三'],
  senior: ['高一', '高二', '高三'],
  university: ['大一', '大二', '大三', '大四']
}
const allPresetList = [
  ...presetGrades.kindergarten,
  ...presetGrades.primary,
  ...presetGrades.junior,
  ...presetGrades.senior,
  ...presetGrades.university
]

const selectedGrades = ref([])
const customAddedGrades = ref([])
const newCustomGrade = ref('')
const currentSemesterGrades = ref([])

const togglePresetSection = (section) => {
  const list = presetGrades[section]
  const allSelected = list.every(g => selectedGrades.value.includes(g))
  if (allSelected) {
    selectedGrades.value = selectedGrades.value.filter(g => !list.includes(g))
  } else {
    list.forEach(g => {
      if (!selectedGrades.value.includes(g)) {
        selectedGrades.value.push(g)
      }
    })
  }
}

const addCustomGrade = () => {
  const val = newCustomGrade.value.trim()
  if (!val) return
  if (allPresetList.includes(val)) {
    if (!selectedGrades.value.includes(val)) {
      selectedGrades.value.push(val)
    }
  } else {
    if (!customAddedGrades.value.includes(val)) {
      customAddedGrades.value.push(val)
    }
    if (!selectedGrades.value.includes(val)) {
      selectedGrades.value.push(val)
    }
  }
  newCustomGrade.value = ''
}

const removeCustomGrade = (grade) => {
  customAddedGrades.value = customAddedGrades.value.filter(g => g !== grade)
  selectedGrades.value = selectedGrades.value.filter(g => g !== grade)
}

const rules = {
  name: [{ required: true, message: '请输入学期名称', trigger: 'blur' }],
  start_date: [{ required: true, message: '请选择开始日期', trigger: 'change' }],
  end_date: [{ required: true, message: '请选择结束日期', trigger: 'change' }]
}

const fetchSemesters = async () => {
  loading.value = true
  try { semesters.value = await semesterApi.getAll() }
  catch (e) { console.error(e) }
  finally { loading.value = false }
}

const showCreateDialog = () => {
  isEdit.value = false
  editingId.value = null
  form.value = { name: '', start_date: '', end_date: '', is_active: false, available_grades: defaultAvailableGrades }
  selectedGrades.value = defaultAvailableGrades.split(',')
  customAddedGrades.value = []
  dialogVisible.value = true
}

const editSemester = (row) => {
  isEdit.value = true
  editingId.value = row.id
  form.value = { ...row }
  
  const grades = row.available_grades ? row.available_grades.split(',').map(g => g.trim()).filter(g => g.length > 0) : []
  selectedGrades.value = [...grades]
  customAddedGrades.value = grades.filter(g => !allPresetList.includes(g))
  
  dialogVisible.value = true
}

const submitForm = async () => {
  if (!formRef.value) return
  await formRef.value.validate(async (valid) => {
    if (!valid) return
    if (selectedGrades.value.length === 0) {
      ElMessage.warning('请至少配置一个年级')
      return
    }
    form.value.available_grades = selectedGrades.value.join(',')
    
    submitting.value = true
    try {
      if (isEdit.value) {
        await semesterApi.update(editingId.value, form.value)
        ElMessage.success('学期更新成功')
      } else {
        await semesterApi.create(form.value)
        ElMessage.success('学期创建成功')
      }
      dialogVisible.value = false
      fetchSemesters()
    } catch (e) { console.error(e) }
    finally { submitting.value = false }
  })
}

const setActive = async (row) => {
  try {
    await semesterApi.update(row.id, { is_active: true })
    ElMessage.success('已设为当前学期')
    fetchSemesters()
  } catch (e) { console.error(e) }
}

const deleteSemester = async (id) => {
  try {
    await semesterApi.delete(id)
    ElMessage.success('学期已删除')
    fetchSemesters()
  } catch (e) { console.error(e) }
}

// ============ 班级教师配置 ============
const teacherDialogVisible = ref(false)
const teacherLoading = ref(false)
const teacherSaving = ref(false)
const currentSemester = ref(null)
const teacherGrade = ref('')
const classCount = ref(6)
const teacherGrid = ref([])

const configSubjects = ['语文', '数学', '英语', '物理', '化学', '生物', '政治', '历史', '地理']

const buildTeacherGrid = () => {
  // 保留已有的输入数据
  const existing = {}
  teacherGrid.value.forEach(row => { existing[row.class_name] = row.teachers })

  teacherGrid.value = Array.from({ length: classCount.value }, (_, i) => {
    const className = `${i + 1}班`
    return {
      class_name: className,
      teachers: { ...(existing[className] || {}) }
    }
  })
}

const openTeacherConfig = async (semester) => {
  currentSemester.value = semester
  
  if (semester.available_grades) {
    currentSemesterGrades.value = semester.available_grades.split(',').map(g => g.trim()).filter(g => g.length > 0)
  } else {
    currentSemesterGrades.value = []
  }
  
  teacherGrade.value = currentSemesterGrades.value[0] || ''
  classCount.value = 6
  buildTeacherGrid()
  teacherDialogVisible.value = true

  // 加载已有配置
  teacherLoading.value = true
  try {
    const data = await classTeacherApi.getAll(semester.id)
    // 按年级过滤并填入 grid
    const gradeData = data.filter(d => d.grade === teacherGrade.value)

    // 自动推断班级数
    const classes = [...new Set(gradeData.map(d => d.class_name))]
    if (classes.length > 0) {
      const maxClass = Math.max(...classes.map(c => parseInt(c) || 0))
      if (maxClass > classCount.value) {
        classCount.value = maxClass
        buildTeacherGrid()
      }
    }
    // 填入教师数据
    gradeData.forEach(d => {
      const row = teacherGrid.value.find(r => r.class_name === d.class_name)
      if (row) row.teachers[d.subject] = d.teacher_name
    })
  } catch (e) { console.error(e) }
  finally { teacherLoading.value = false }
}

const saveTeacherConfig = async () => {
  if (!currentSemester.value) return
  teacherSaving.value = true
  try {
    const payload = []
    teacherGrid.value.forEach(row => {
      configSubjects.forEach(subj => {
        const name = (row.teachers[subj] || '').trim()
        if (name) {
          payload.push({
            grade: teacherGrade.value,
            class_name: row.class_name,
            subject: subj,
            teacher_name: name
          })
        }
      })
    })
    const result = await classTeacherApi.batchSave(currentSemester.value.id, payload)
    ElMessage.success(result.message)
  } catch (e) { console.error(e) }
  finally { teacherSaving.value = false }
}

onMounted(fetchSemesters)
</script>

<style scoped>
.semester-management {
  max-width: 1200px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-header span {
  font-size: 16px;
  font-weight: 500;
}

.teacher-config-header {
  margin-bottom: 12px;
}

.teacher-grid-wrap {
  max-height: 55vh;
  overflow-y: auto;
}

/* 年级配置面板样式 */
.grade-group-row {
  margin-bottom: 12px;
}

.grade-group-row:last-child {
  margin-bottom: 0;
}

.grade-group-title {
  font-size: 11px;
  font-weight: bold;
  color: #909399;
  margin-bottom: 6px;
  user-select: none;
}

.grade-group-title.custom {
  color: #e6a23c;
}

.grade-checkbox-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
}

.grade-checkbox {
  margin: 0 !important;
  height: auto !important;
}

.grade-checkbox :deep(.el-checkbox__label) {
  padding-left: 6px;
  font-size: 13px;
  white-space: nowrap;
}

.custom-grade-label {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  white-space: nowrap;
}

.remove-btn {
  color: #f56c6c;
  cursor: pointer;
  font-weight: bold;
  font-size: 14px;
  transition: color 0.2s;
  padding: 0 2px;
}

.remove-btn:hover {
  color: #f56c6c;
  background-color: rgba(245, 108, 108, 0.1);
  border-radius: 4px;
}
</style>
